/**
 * SectionCard Component
 * 
 * A styled section container with optional title and icon.
 * Used for grouping related form fields or content.
 */

import React from 'react';
import type { LucideIcon } from 'lucide-react';

export type SectionCardVariant = 'gray' | 'blue' | 'green' | 'amber' | 'purple' | 'red';

export interface SectionCardProps {
  /** Section title */
  title?: string;
  /** Optional description below title */
  description?: string;
  /** Optional icon to display with title */
  icon?: LucideIcon;
  /** Color variant */
  variant?: SectionCardVariant;
  /** Whether to show a border */
  bordered?: boolean;
  /** Section content */
  children: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Header right side content (e.g., toggle, button) */
  headerAction?: React.ReactNode;
}

const variantStyles: Record<SectionCardVariant, { bg: string; border: string; icon: string }> = {
  gray: { bg: 'bg-gray-50', border: 'border-gray-200', icon: 'text-gray-600' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-100', icon: 'text-blue-600' },
  green: { bg: 'bg-green-50', border: 'border-green-100', icon: 'text-green-600' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-100', icon: 'text-amber-600' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-100', icon: 'text-purple-600' },
  red: { bg: 'bg-red-50', border: 'border-red-100', icon: 'text-red-600' },
};

export function SectionCard({
  title,
  description,
  icon: Icon,
  variant = 'gray',
  bordered = false,
  children,
  className = '',
  headerAction,
}: SectionCardProps) {
  const styles = variantStyles[variant];
  const borderClass = bordered ? `border ${styles.border}` : '';
  
  return (
    <div className={`${styles.bg} p-4 rounded-lg ${borderClass} ${className}`}>
      {(title || headerAction) && (
        <div className="flex items-start justify-between mb-3">
          <div>
            <h4 className="font-medium text-gray-900 flex items-center">
              {Icon && <Icon className={`w-4 h-4 mr-2 ${styles.icon}`} />}
              {title}
            </h4>
            {description && (
              <p className="text-sm text-gray-500 mt-1">{description}</p>
            )}
          </div>
          {headerAction}
        </div>
      )}
      {children}
    </div>
  );
}
